// `React` must stay in scope: this project lints for the classic JSX runtime
// (react/react-in-jsx-scope), as every other component here does.
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import {
  TURNSTILE_ERROR,
  TURNSTILE_LABEL,
  TURNSTILE_PENDING_HINT,
  TURNSTILE_RETRY_LABEL
} from './constants';
import type { RequestAccessVariant } from './types';

import styles from './styles.module.css';

import { track } from '@/shared/analytics/track';
import type { TurnstileApi } from '@/shared/security/turnstile';
import { loadTurnstile } from '@/shared/security/turnstile';

export interface TurnstileHandle {
  /** Re-runs the challenge and issues a fresh token. */
  reset: () => void;
}

/** Only the outcomes that change what the user sees. */
type Status = 'pending' | 'ok' | 'error';

interface TurnstileFieldProps {
  siteKey: string;
  /** A fresh token on success; null once it expires, times out, or fails. */
  onChange: (token: string | null) => void;
  /** The script itself never loaded — blocked, offline, or a CF incident. */
  onUnavailable: () => void;
  variant: RequestAccessVariant;
}

const TurnstileField = forwardRef<TurnstileHandle, TurnstileFieldProps>(
  ({ siteKey, onChange, onUnavailable, variant }, ref) => {
    const [status, setStatus] = useState<Status>('pending');
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const apiRef = useRef<TurnstileApi | null>(null);

    // The parent watch()es email and company, so it re-renders on every
    // keystroke and these inline callbacks change identity constantly. Reading
    // them through a ref keeps the render effect's dep list at [siteKey];
    // otherwise the widget is torn down and re-created on every character
    // typed, which looks broken and burns challenge quota. Refs are exempt from
    // exhaustive-deps, so this needs no disable comment.
    const handlers = useRef({ onChange, onUnavailable, variant });
    useEffect(() => {
      handlers.current = { onChange, onUnavailable, variant };
    });

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;

      let cancelled = false;

      loadTurnstile()
        .then((api) => {
          // Both guards matter: StrictMode's development double-invoke can
          // resolve a second promise after cleanup has run, and render() on a
          // container that already holds a widget is an error.
          if (cancelled || widgetIdRef.current !== null) return;

          apiRef.current = api;
          widgetIdRef.current =
            api.render(container, {
              sitekey: siteKey,
              // Logged by b2b-api today; it can start enforcing the value once
              // the data shows every client sets it.
              action: 'request-access',
              // Not 'auto' — ThemeContext hardcodes light, and the hero card is
              // a pale sage green.
              theme: 'light',
              size: 'normal',
              'response-field': false,
              callback: (token) => {
                setStatus('ok');
                handlers.current.onChange(token);
              },
              // Turnstile refreshes an expired token by itself, so this is a
              // normal part of a long-lived page rather than a failure.
              'expired-callback': () => {
                setStatus('pending');
                handlers.current.onChange(null);
              },
              'timeout-callback': () => {
                setStatus('error');
                handlers.current.onChange(null);
                track('request_access_verification_failed', {
                  variant: handlers.current.variant,
                  reason: 'timeout',
                  verification_error_code: null
                });
              },
              'unsupported-callback': () => {
                setStatus('error');
                handlers.current.onChange(null);
                track('request_access_verification_failed', {
                  variant: handlers.current.variant,
                  reason: 'unsupported',
                  verification_error_code: null
                });
              },
              'error-callback': (code) => {
                setStatus('error');
                handlers.current.onChange(null);
                track('request_access_verification_failed', {
                  variant: handlers.current.variant,
                  reason: 'error',
                  verification_error_code: code ?? null
                });
                // Suppress Cloudflare's own message so the copy stays in our
                // voice — which obliges us to offer the retry below.
                return true;
              }
            }) ?? null;
        })
        .catch(() => {
          if (cancelled) return;
          track('request_access_verification_failed', {
            variant: handlers.current.variant,
            reason: 'script_blocked',
            verification_error_code: null
          });
          // Hand the form back to the user rather than leaving it inert. The
          // server is the enforcement point, so this costs no security.
          handlers.current.onUnavailable();
        });

      return () => {
        cancelled = true;
        const id = widgetIdRef.current;
        widgetIdRef.current = null;
        if (id !== null) {
          try {
            apiRef.current?.remove(id);
          } catch {
            // Already gone; nothing to clean up.
          }
        }
      };
    }, [siteKey]);

    // Shared by the parent's post-failure reset and the retry button below.
    const resetWidget = () => {
      const id = widgetIdRef.current;
      if (id === null) return;
      setStatus('pending');
      try {
        apiRef.current?.reset(id);
      } catch {
        // Nothing to reset; the next mount will render a fresh widget.
      }
    };

    useImperativeHandle(ref, () => ({ reset: resetWidget }));

    return (
      <div
        className={styles.turnstile}
        role="group"
        aria-label={TURNSTILE_LABEL}
      >
        {/* Must stay a childless React leaf: Turnstile appends its iframe here
            and React must never try to reconcile this node's children. */}
        <div className={styles.turnstileBox} ref={containerRef} />

        {status === 'pending' && (
          <p className={styles.help}>{TURNSTILE_PENDING_HINT}</p>
        )}

        {status === 'error' && (
          <>
            {/* role="alert" announces without stealing focus — the user may be
                mid-typing, so moving focus here would be hostile. */}
            <p className={styles.error} role="alert">
              {TURNSTILE_ERROR}
            </p>
            <button
              type="button"
              className={styles.turnstileRetry}
              onClick={resetWidget}
            >
              {TURNSTILE_RETRY_LABEL}
            </button>
          </>
        )}
      </div>
    );
  }
);

TurnstileField.displayName = 'TurnstileField';

export default TurnstileField;
